import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'

export default function LandingPage() {
  const navigate = useNavigate()
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const [isSigningIn, setIsSigningIn] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    // Check if user is already logged in
    const checkUser = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (session?.user) {
          navigate('/app')
        }
      } catch (error) {
        console.error('Error checking auth:', error)
      } finally {
        setIsCheckingAuth(false)
      }
    }

    checkUser()
  }, [navigate])

  const handleGoogleSignIn = async () => {
    setIsSigningIn(true)
    setError(null)

    try {
      const { error: signInError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      })

      if (signInError) {
        throw signInError
      }
    } catch (err) {
      setError('Failed to sign in. Please try again.')
      setIsSigningIn(false)
      console.error('Sign in error:', err)
    }
  }

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-950 via-gray-900 to-blue-950">
        <div className="text-gray-400">Loading...</div>
      </div>
    )
  }

  return (
    <>
      <main className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-blue-950 text-white">
        {/* Hero Section - Above the Fold */}
        <div className="min-h-screen flex items-center justify-center px-6">
          <div className="max-w-3xl text-center space-y-8">
            <div className="flex items-center justify-center gap-4">
              <img
                src="/logos/gjyl-logo-oauth-512.png"
                alt="GJYL"
                className="w-20 h-20 rounded-xl drop-shadow-2xl"
              />
              <h1 className="text-7xl font-bold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                GJYL
              </h1>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-gray-100 leading-tight">
              Chat built for real connection.
              <br />
              <span className="text-blue-400">Not for selling your attention.</span>
            </h2>

            <p className="text-xl text-gray-300 leading-relaxed max-w-2xl mx-auto">
              27 years of chat experience distilled into one truth: <strong>no ads, no algorithms, no surveillance.</strong> Just you and the people who matter.
            </p>

            {/* CTA Section */}
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 max-w-md mx-auto">
              <h3 className="text-2xl font-semibold text-white mb-6">
                Get Started
              </h3>

              {error && (
                <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}

              <button
                onClick={handleGoogleSignIn}
                disabled={isSigningIn}
                className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-100 text-gray-900 font-medium py-3 px-4 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSigningIn ? (
                  <>
                    <div className="w-5 h-5 border-2 border-gray-900/30 border-t-gray-900 rounded-full animate-spin"></div>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    <span>Continue with Google</span>
                  </>
                )}
              </button>

              <p className="text-xs text-gray-400 text-center mt-6">
                Free • No credit card • Sign in with Google
              </p>

              <div className="mt-6 text-center text-sm text-gray-400">
                Don't have an account?{' '}
                <Link to="/signup" className="text-blue-400 hover:text-blue-300 font-medium transition-colors">
                  Sign up
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Details Section - Below the Fold */}
        <div className="max-w-5xl mx-auto px-6 pb-20">

          {/* What GJYL Is */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">💬</div>
              <h3 className="text-lg font-bold text-gray-100 mb-2">Real Conversations</h3>
              <p className="text-gray-300 text-sm">
                DMs and groups without character limits or read receipts tracking you.
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">🏠</div>
              <h3 className="text-lg font-bold text-gray-100 mb-2">Your Spaces</h3>
              <p className="text-gray-300 text-sm">
                Private communities with organized channels. You control who's in.
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">🔒</div>
              <h3 className="text-lg font-bold text-gray-100 mb-2">Privacy First</h3>
              <p className="text-gray-300 text-sm">
                Your data isn't sold. No AI training. No tracking. Built with respect.
              </p>
            </div>
          </div>

          {/* The Story */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-10 mb-12">
            <h3 className="text-2xl font-bold text-gray-100 mb-6">What happened to just... talking?</h3>
            <div className="space-y-4 text-gray-300 leading-relaxed">
              <p>
                I've been on chat programs since the late '90s. Made lifelong friends on AIM and IRC.
                Stayed connected with people I grew up with. Chat used to be simple: open the app,
                your friends are there, you talk.
              </p>
              <p>
                Then social media happened. Everything became a feed. Algorithms decided who you see.
                Companies harvested your data. Ads everywhere. The platforms stopped being about
                <strong> you and your friends</strong>—they became about <strong>keeping you on the platform</strong>.
              </p>
              <p className="text-blue-400 font-semibold">
                GJYL is a return to what mattered: private spaces, real conversations, connections that
                last decades—not dopamine hits that last seconds.
              </p>
            </div>
          </div>

          {/* Who Built This */}
          <div className="bg-gradient-to-r from-blue-950/50 to-purple-950/50 backdrop-blur-xl border border-blue-500/20 rounded-2xl p-10">
            <h3 className="text-2xl font-bold text-gray-100 mb-4">Who's building this?</h3>
            <p className="text-gray-300 leading-relaxed mb-4">
              I'm Paul. Full-stack developer. Been in chat rooms since 1997. After my father passed,
              I decided to stop thinking about "what chat should be" and start building it.
            </p>
            <p className="text-gray-300 leading-relaxed">
              <strong className="text-white">No ads. No surveillance. No algorithm.</strong> Just you and
              the people who matter.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-black/40 backdrop-blur border-t border-white/10 px-6 py-8">
        <div className="max-w-5xl mx-auto text-center text-sm text-gray-400">
          <div className="mb-4">
            <span>© {new Date().getFullYear()} GJYL</span>
            <span className="mx-3">•</span>
            <Link to="/terms" className="hover:text-blue-400 transition-colors">Terms</Link>
            <span className="mx-3">•</span>
            <Link to="/privacy" className="hover:text-blue-400 transition-colors">Privacy</Link>
          </div>
          <p className="text-xs text-gray-500">
            Built with purpose. Not for profit extraction.
          </p>
        </div>
      </footer>
    </>
  )
}
